import { AppBar } from "@progress/kendo-react-layout";
import { SvgIcon } from "@progress/kendo-react-common";
import { userOutlineIcon, logoutIcon } from "@progress/kendo-svg-icons";
import ConfirmModal from "src/components/ConfirmModal/ConfirmModal.tsx";
import { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { useQueryClient } from "@tanstack/react-query";
import DialogBox from "src/components/DialogBox/DialogBox.tsx";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { Button } from "@progress/kendo-react-buttons";
import * as styles from "./MenuNavContainer.css.ts";
import { Link, useLocation } from "react-router";
import { Menu, MenuItem } from "@progress/kendo-react-layout";
import { PAGETITLES } from "src/utils/constants.ts";
import { featureFlags, isFeatureEnabled } from "src/utils/featureFlag.ts";
import { usePermissions } from "src/contexts/PermissionsContext.ts";

const MenuNavContainer = ({ userName }: { userName: string }) => {
  const [logoutConfirm, setLogoutConfirm] = useState(false);
  const { instance: msalInstance } = useMsal();
  const queryClient = useQueryClient();
  const { can } = usePermissions();
  const { pathname } = useLocation();

  // The browser tab names the page you are on (user feedback 2026-10-15);
  // every tab used to read "Retail Pricing". Filled by buildItem below as the
  // menu renders, so the tab and the menu entry can never disagree. A detail
  // route takes its section's name by longest prefix (/pricing/family-maint/
  // family/123 -> Family Maintenance).
  const titlesByRoute = new Map<string, string>();

  // Two separate gates per menu item:
  //   isAvailable(flag) — feature-flag rollout; controls whether the item
  //                       shows at all (still hidden when not rolled out).
  //   hasGrant(table)   — RP_ROLE_TABLES SELECT on the underlying table;
  //                       controls enabled vs. disabled (greyed-out).
  //
  // Mirrors legacy Oracle Forms behaviour: forms the user has no SELECT
  // grant on appear in the menu but are greyed out. Hiding entirely was
  // surprising — if the /api/user/UserPermissions endpoint is briefly
  // unavailable, hide-on-deny would empty the whole nav. Show + disable
  // degrades gracefully.
  //
  // Pass `null` for the table when an item isn't gated by a specific
  // RP_ROLE_TABLES row (BatchStatus, Admin, Suppliers landing page).
  const hasGrant = (table: string | null): boolean =>
    table === null || can(table, "Select");

  const handleLogout = () => {
    setLogoutConfirm(true);
  };

  const handleConfirmLogout = () => {
    queryClient.clear();
    msalInstance.logout();
  };

  const handleCloseLogout = () => {
    setLogoutConfirm(false);
  };

  // Renders either a real <Link> (granted) or a plain <span> with the
  // disabled style (ungranted). Kendo MenuItem's `disabled` prop greys
  // the row and blocks its onSelect, but Kendo doesn't intercept
  // navigation clicks on children — so a real Link inside a disabled
  // MenuItem would still navigate. Switching to a span when disabled is
  // the actual prevention. The native `title` attribute on the rendered
  // element gives the user a hover explanation for why it's greyed out.
  const navLinkRender = (
    route: string,
    text: string,
    disabled: boolean,
    deniedTooltip?: string,
  ) => {
    return function innerNavLinkRender() {
      if (disabled) {
        return (
          <span
            className={styles.navLink}
            style={{ opacity: 0.5, cursor: "not-allowed" }}
            title={deniedTooltip}
          >
            {text}
          </span>
        );
      }
      return (
        <Link className={styles.navLink} to={route}>
          {text}
        </Link>
      );
    };
  };

  // Builds a leaf MenuItem with the show / disable / tooltip behaviour
  // wired up. Returns null when the feature flag isn't enabled so the
  // call site can spread it directly into the Menu tree.
  const buildItem = (
    flag: boolean,
    table: string | null,
    text: string,
    route: string,
  ): React.ReactElement | null => {
    titlesByRoute.set(route, text);
    if (!flag) return null;
    const granted = hasGrant(table);
    const deniedTooltip =
      !granted && table
        ? `You don't have SELECT permission on ${table}.`
        : undefined;
    return (
      <MenuItem
        text={text}
        disabled={!granted}
        linkRender={navLinkRender(route, text, !granted, deniedTooltip)}
      />
    );
  };

  // Runs after this render, by which point the JSX below has called buildItem
  // for every entry and titlesByRoute is complete.
  useEffect(() => {
    let best = "";
    for (const route of titlesByRoute.keys()) {
      if (
        route.length > best.length &&
        (pathname === route || pathname.startsWith(route + "/"))
      )
        best = route;
    }
    const page = best ? titlesByRoute.get(best) : undefined;
    document.title = page ? `${page} - Retail Pricing` : "Retail Pricing";
  });

  return (
    <AppBar className={styles.nav} aria-label="Navigation bar">
      <div>
        <div className={styles.navWrapper}>
          <Link to={"/"}>
            {/* Same URL as the static shell logo in index.html, so it's served
                from cache here — no flash or extra request when React mounts. */}
            <img src="/RpLogo.svg" alt="RP Logo" className={styles.logoImage} />
          </Link>
          <Menu className={styles.navLinkContainer}>
            <MenuItem
              cssClass={styles.navLinkHeader}
              text={PAGETITLES.BatchStatus}
            >
              {buildItem(true, null, PAGETITLES.BatchStatus, "/batch-upload/status")}
              {buildItem(true, null, PAGETITLES.BatchUpload, "/batch-upload/create")}
            </MenuItem>

            {(isFeatureEnabled(featureFlags.ItemMaintenance) ||
              isFeatureEnabled(featureFlags.ItemAuthorizations) ||
              isFeatureEnabled(featureFlags.ItemStateFlag) ||
              isFeatureEnabled(featureFlags.PseudoUpc) ||
              isFeatureEnabled(featureFlags.MaintainStoreCoupons) ||
              isFeatureEnabled(featureFlags.MiscellaneousTransactionCodes) ||
              isFeatureEnabled(featureFlags.ScaleDepartments) ||
              isFeatureEnabled(featureFlags.ScaleTareCodes) ||
              isFeatureEnabled(featureFlags.CaseInterpackItem) ||
              isFeatureEnabled(featureFlags.UpcRecalls) ||
              isFeatureEnabled(featureFlags.ShipperMaint)) && (
              <MenuItem cssClass={styles.navLinkHeader} text="Items">
                {buildItem(isFeatureEnabled(featureFlags.ItemMaintenance), "RETAIL_ITEM", PAGETITLES.ItemMaintenance, "/items/maintenance")}
                {buildItem(isFeatureEnabled(featureFlags.ItemAuthorizations), "RETAIL_ITEM_AUTHORIZATION", PAGETITLES.ItemAuthorization, "/items/item-auth")}
                {buildItem(isFeatureEnabled(featureFlags.ItemStateFlag), "RETAIL_ITEM", PAGETITLES.ItemState, "/items/item-state-flag")}
                {buildItem(isFeatureEnabled(featureFlags.PseudoUpc), "SUPPLIER_ITEM", PAGETITLES.PseudoUpc, "/items/pseudo-upcs")}
                {buildItem(isFeatureEnabled(featureFlags.MaintainStoreCoupons), "RETAIL_ITEM", PAGETITLES.MaintainStoreCoupons, "/items/maintain-store-coupons")}
                {buildItem(isFeatureEnabled(featureFlags.MiscellaneousTransactionCodes), "RETAIL_ITEM", PAGETITLES.MiscellaneousTransactionCodes, "/items/misc-transaction-codes")}
                {buildItem(isFeatureEnabled(featureFlags.ScaleDepartments), "SCALE_DEPARTMENT", PAGETITLES.ScaleDepartments, "/items/scale-departments")}
                {buildItem(isFeatureEnabled(featureFlags.ScaleTareCodes), "SCALE_TARE", PAGETITLES.ScaleTareCodes, "/items/scale-tare-codes")}
                {buildItem(isFeatureEnabled(featureFlags.CaseInterpackItem), "RETAIL_ITEM", PAGETITLES.CaseInterpackItem, "/items/case-interpak")}
                {buildItem(isFeatureEnabled(featureFlags.UpcRecalls), "UPC_PRODUCT_RECALL", PAGETITLES.UpcRecalls, "/items/upc-recalls")}
              </MenuItem>
            )}

            {(isFeatureEnabled(featureFlags.TagHandlingInstructions) ||
              isFeatureEnabled(featureFlags.TagReplacementStoreRequest) ||
              isFeatureEnabled(featureFlags.TagFullScanShelfTagRequest)) && (
              <MenuItem cssClass={styles.navLinkHeader} text="Tags">
                {buildItem(isFeatureEnabled(featureFlags.TagHandlingInstructions), "TAG_HANDLING_INSTR", PAGETITLES.TagHandlingInstructions, "/items/tag-handling-instructions")}
                {buildItem(isFeatureEnabled(featureFlags.TagReplacementStoreRequest), "PENDING_TAG", PAGETITLES.TagReplacementStoreRequest, "/items/tag-requests/replacement-store")}
                {buildItem(isFeatureEnabled(featureFlags.TagFullScanShelfTagRequest), "PENDING_TAG", PAGETITLES.TagFullScanShelfTagRequest, "/items/tag-requests/full-scan")}
              </MenuItem>
            )}

            {(isFeatureEnabled(featureFlags.DisplayPriceDetails) ||
              isFeatureEnabled(featureFlags.FamilyMaint) ||
              isFeatureEnabled(featureFlags.RegularPricing) ||
              isFeatureEnabled(featureFlags.PriceGroupMove) ||
              isFeatureEnabled(featureFlags.PromoPricing) ||
              isFeatureEnabled(featureFlags.AddPendingPricing)) && (
              <MenuItem cssClass={styles.navLinkHeader} text={PAGETITLES.Pricing}>
                {buildItem(isFeatureEnabled(featureFlags.DisplayPriceDetails), "RETAIL_PRICE", PAGETITLES.DisplayPriceDetails, "/pricing/display-price-details")}
                {buildItem(isFeatureEnabled(featureFlags.FamilyMaint), "FAMILY", PAGETITLES.FamilyMaint, "/pricing/family-maint")}
                {buildItem(isFeatureEnabled(featureFlags.RegularPricing), "RETAIL_PRICE", PAGETITLES.RegularPricing, "/pricing/regular-by-banner")}
                {buildItem(isFeatureEnabled(featureFlags.PriceGroupMove), "RETAIL_ITEM", PAGETITLES.PriceGroupMove, "/pricing/price-group-moves")}
                {buildItem(isFeatureEnabled(featureFlags.PromoPricing), "RETAIL_PRICE", PAGETITLES.PromoPricing, "/pricing/promo-by-banner")}
                {buildItem(isFeatureEnabled(featureFlags.AddPendingPricing), "RETAIL_PRICE", PAGETITLES.AddPendingPricing, "/pricing/add-future")}
              </MenuItem>
            )}

            {(isFeatureEnabled(featureFlags.Stores) ||
              isFeatureEnabled(featureFlags.ZoneBanners) ||
              isFeatureEnabled(featureFlags.StoreSuppliers) ||
              isFeatureEnabled(featureFlags.StoreZones) ||
              isFeatureEnabled(featureFlags.StoreDeptXrefs) ||
              isFeatureEnabled(featureFlags.CopyStoreDSD) ||
              isFeatureEnabled(featureFlags.PosFileCompare) ||
              isFeatureEnabled(featureFlags.CopyStoreZones)) && (
              <MenuItem cssClass={styles.navLinkHeader} text="Stores">
                {buildItem(isFeatureEnabled(featureFlags.Stores), "STORE", PAGETITLES.Stores, "/stores")}
                {buildItem(isFeatureEnabled(featureFlags.ZoneBanners), null, PAGETITLES.ZoneBanners, "/stores/zone-banners")}
                {buildItem(isFeatureEnabled(featureFlags.StoreSuppliers), "STORE_SUPPLIER", PAGETITLES.StoreSuppliers, "/stores/store-suppliers")}
                {buildItem(isFeatureEnabled(featureFlags.StoreZones), "SYSTEM_ZONE_SUPPLIER_CUST", PAGETITLES.StoreZones, "/stores/store-zones")}
                {buildItem(isFeatureEnabled(featureFlags.StoreDeptXrefs), "STORE_DEPT_CATEGORY_CODE", PAGETITLES.StoreDeptXrefs, "/stores/store-dept-xrefs")}
                {buildItem(isFeatureEnabled(featureFlags.CopyStoreDSD), "STORE_SUPPLIER", PAGETITLES.CopyStoreDSD, "/stores/copy-store-dsd")}
                {buildItem(isFeatureEnabled(featureFlags.PosFileCompare), "STORE", PAGETITLES.PosFileCompare, "/stores/pos-file-compare")}
                {buildItem(isFeatureEnabled(featureFlags.CopyStoreZones), "SYSTEM_ZONE_SUPPLIER_CUST", PAGETITLES.CopyStoreZones, "/stores/copy-store-zones")}
              </MenuItem>
            )}

            {isFeatureEnabled(featureFlags.Suppliers) && (() => {
              const granted = hasGrant("SUPPLIER");
              return (
                <MenuItem
                  cssClass={styles.navLinkHeader}
                  text="Suppliers"
                  disabled={!granted}
                  linkRender={() =>
                    granted ? (
                      <Link className={styles.navLinkSingle} to={"/suppliers"}>
                        Suppliers
                      </Link>
                    ) : (
                      <span
                        className={styles.navLinkSingle}
                        style={{ opacity: 0.5, cursor: "not-allowed" }}
                        title="You don't have SELECT permission on SUPPLIER."
                      >
                        Suppliers
                      </span>
                    )
                  }
                />
              );
            })()}

            {isFeatureEnabled(featureFlags.Admin) && (
              <MenuItem cssClass={styles.navLinkHeader} text={PAGETITLES.Admin}>
                {buildItem(true, null, PAGETITLES.StateSubgroups, "/admin/state-subgroups")}
                {buildItem(isFeatureEnabled(featureFlags.SecurityManagement), null, PAGETITLES.SecurityManagement, "/admin/security")}
                {/* Journal viewer is gated server-side per-table — the
                    /tables endpoint filters to grants the user holds,
                    so the menu item itself stays ungated and the inner
                    UI simply shows an empty list for users with no
                    journal SELECTs. */}
                {buildItem(isFeatureEnabled(featureFlags.Journal), null, PAGETITLES.Journal, "/admin/journal")}
                {buildItem(
                  isFeatureEnabled(featureFlags.PromotionTypesRules) ||
                    isFeatureEnabled(featureFlags.PromotionBenchmarkCodes) ||
                    isFeatureEnabled(featureFlags.PromotionItemTypes) ||
                    isFeatureEnabled(featureFlags.PromotionGroups) ||
                    isFeatureEnabled(featureFlags.RewardPromotionStatus) ||
                    isFeatureEnabled(featureFlags.AdDisplayPlan) ||
                    isFeatureEnabled(featureFlags.ScanbackEntryTypes) ||
                    isFeatureEnabled(featureFlags.AdType) ||
                    isFeatureEnabled(featureFlags.SecondaryPromoLinks) ||
                    isFeatureEnabled(featureFlags.ScanbackBusinessUnits) ||
                    isFeatureEnabled(featureFlags.AdvertisedItemCodes) ||
                    isFeatureEnabled(featureFlags.PromotionActionCodes) ||
                    isFeatureEnabled(featureFlags.AdPriceFormat) ||
                    isFeatureEnabled(featureFlags.DistributionMethod) ||
                    isFeatureEnabled(featureFlags.AdLocation) ||
                    isFeatureEnabled(featureFlags.RuleOperationCodes) ||
                    isFeatureEnabled(featureFlags.AccountPromotionXref) ||
                    isFeatureEnabled(featureFlags.RuleBaseCode) ||
                    isFeatureEnabled(featureFlags.PriceBookCodes) ||
                    isFeatureEnabled(featureFlags.PriceBasis) ||
                    isFeatureEnabled(featureFlags.EdlpDeleteDate) ||
                    isFeatureEnabled(featureFlags.PriceReasonCodes) ||
                    isFeatureEnabled(featureFlags.PriceStatusCodes) ||
                    isFeatureEnabled(featureFlags.CompetitorBanners) ||
                    isFeatureEnabled(featureFlags.RuleTargetCodes),
                  null,
                  PAGETITLES.ValidationList,
                  "/admin/validation-list",
                )}
              </MenuItem>
            )}

            {(isFeatureEnabled(featureFlags.ShipperCost) ||
              isFeatureEnabled(featureFlags.DealCost) ||
              isFeatureEnabled(featureFlags.DisplayCostDetails) ||
              isFeatureEnabled(featureFlags.DSDCostLink)) && (
              <MenuItem cssClass={styles.navLinkHeader} text="Cost">
                {buildItem(isFeatureEnabled(featureFlags.ShipperCost), "ITEM_COST_ZONE_COST", PAGETITLES.ShipperCost, "/cost/shipper-cost")}
                {buildItem(isFeatureEnabled(featureFlags.DealCost), "ITEM_COST_ZONE_ALLOW", PAGETITLES.DealCost, "/cost/deal-cost")}
                {buildItem(isFeatureEnabled(featureFlags.DisplayCostDetails), "ITEM_COST_HISTORY", PAGETITLES.DisplayCostDetails, "/cost/display-cost-details")}
                {buildItem(isFeatureEnabled(featureFlags.DSDCostLink), "SUPPLIER_ITEM", PAGETITLES.DSDCostLink, "/cost/dsd-cost-link")}
              </MenuItem>
            )}

            {(isFeatureEnabled(featureFlags.Promotions) ||
              isFeatureEnabled(featureFlags.AdGroups) ||
              isFeatureEnabled(featureFlags.AdBlocks) ||
              isFeatureEnabled(featureFlags.Scanbacks) ||
              isFeatureEnabled(featureFlags.SecondaryPromotions) ||
              isFeatureEnabled(featureFlags.SpecialtyPromotions) ||
              isFeatureEnabled(featureFlags.MaintainPromotionPrices) ||
              isFeatureEnabled(featureFlags.AddFuturePromotions) ||
              isFeatureEnabled(featureFlags.PromoPricingByBanner) ||
              isFeatureEnabled(featureFlags.EOffers)) && (
              <MenuItem cssClass={styles.navLinkHeader} text="Promotions">
                {buildItem(isFeatureEnabled(featureFlags.Promotions), "PROMOTION", PAGETITLES.Promotions, "/promotions")}
                {buildItem(isFeatureEnabled(featureFlags.AdGroups), "AD_GROUP", PAGETITLES.AdGroups, "/promotions/ad-groups")}
                {buildItem(isFeatureEnabled(featureFlags.AdBlocks), "EVENT_OFFER", PAGETITLES.AdBlocks, "/promotions/ad-blocks")}
                {buildItem(isFeatureEnabled(featureFlags.Scanbacks), "SCANBACK", PAGETITLES.Scanbacks, "/promotions/scanbacks")}
                {buildItem(isFeatureEnabled(featureFlags.SecondaryPromotions), "SECONDARY_PROMOTION", PAGETITLES.SecondaryPromotions, "/promotions/secondary")}
                {buildItem(isFeatureEnabled(featureFlags.SpecialtyPromotions), "SPECIALTY_PROMOTION", PAGETITLES.SpecialtyPromotions, "/promotions/specialty")}
                {buildItem(isFeatureEnabled(featureFlags.MaintainPromotionPrices), "PROMOTION", PAGETITLES.MaintainPromotionPrices, "/promotions/maintain-prices")}
                {buildItem(isFeatureEnabled(featureFlags.AddFuturePromotions), "PROMOTION", PAGETITLES.AddFuturePromotions, "/promotions/add-future")}
                {buildItem(isFeatureEnabled(featureFlags.PromoPricingByBanner), "RETAIL_PRICE", PAGETITLES.PromoPricingByBanner, "/promotions/pricing-by-banner")}
                {buildItem(isFeatureEnabled(featureFlags.EOffers), "EOFFER", PAGETITLES.EOffers, "/promotions/eoffers")}
              </MenuItem>
            )}
          </Menu>
        </div>
      </div>
      <div className={styles.profileOptions}>
        <Link
          to="/profile"
          className={`${styles.usernameHolder} ${styles.usernameLink}`}
          aria-label="View profile"
        >
          <SvgIcon icon={userOutlineIcon} />
          {userName}
        </Link>
        <Tooltip openDelay={100} position="bottom" anchorElement="target">
          <Button
            themeColor="primary"
            title="Logout"
            aria-label="Logout"
            onClick={handleLogout}
            svgIcon={logoutIcon}
          />
        </Tooltip>
      </div>
      <DialogBox
        isOpen={logoutConfirm}
        onClose={handleCloseLogout}
        title="Are you sure you want to Logout?"
      >
        <ConfirmModal
          handleClose={handleCloseLogout}
          handleConfirm={handleConfirmLogout}
        />
      </DialogBox>
    </AppBar>
  );
};

export default MenuNavContainer;
